/**
 * Outcome of running the worker for a single item.
 */
export type ItemOutcome<TItem, TResult> =
  | { item: TItem; ok: true; result: TResult }
  | { item: TItem; ok: false; error: unknown };

/**
 * Runs an async worker over every item with at most `limit` in flight at once. Unlike
 * `Promise.all`, a failing item never aborts the batch: every item runs, and the outcome of each is
 * returned in input order. `onProgress` fires after each item settles with the number settled so far.
 */
export async function runWithConcurrency<TItem, TResult>(
  items: readonly TItem[],
  limit: number,
  worker: (item: TItem) => Promise<TResult>,
  onProgress?: (settled: number, total: number) => void,
): Promise<ItemOutcome<TItem, TResult>[]> {
  const outcomes = new Array<ItemOutcome<TItem, TResult>>(items.length);
  const laneCount = Math.max(1, Math.min(Math.floor(limit), items.length));
  let nextIndex = 0;
  let settled = 0;

  // Each lane pulls the next unclaimed index until the queue is drained.
  const lane = async (): Promise<void> => {
    while (nextIndex < items.length) {
      const index = nextIndex++;
      const item = items[index];
      try {
        outcomes[index] = { item, ok: true, result: await worker(item) };
      } catch (error) {
        outcomes[index] = { item, ok: false, error };
      }
      settled++;
      onProgress?.(settled, items.length);
    }
  };

  await Promise.all(Array.from({ length: laneCount }, lane));
  return outcomes;
}
